import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Heart } from "lucide-react";
import { useAccount } from "@/lib/account";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const BURST = Array.from({ length: 8 }, (_, i) => (i / 8) * Math.PI * 2);

/** Heart with a little burst when you fall in love with a painting. */
export function FavoriteButton({ slug, size = "md", className, withLabel = false }: { slug: string; size?: "sm" | "md" | "lg"; className?: string; withLabel?: boolean }) {
  const { isFavorite, toggleFavorite } = useAccount();
  const { t } = useI18n();
  const [pulse, setPulse] = useState(0);
  const on = isFavorite(slug);
  const dim = size === "lg" ? "size-7" : size === "sm" ? "size-4" : "size-5";
  const pad = size === "lg" ? "p-3.5" : size === "sm" ? "p-2" : "p-2.5";
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={on ? t("fav.remove") : t("fav.add")}
      title={on ? t("fav.remove") : t("fav.add")}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (toggleFavorite(slug)) setPulse((n) => n + 1); }}
      className={cn("relative inline-flex items-center gap-2 rounded-full bg-surface/90 text-ink shadow-md backdrop-blur transition hover:scale-105 active:scale-95", pad, withLabel && "pr-5", className)}
    >
      <motion.span key={`h${pulse}`} initial={pulse ? { scale: 0.6 } : false} animate={{ scale: [0.6, 1.35, 1] }} transition={{ duration: 0.45 }} className="relative grid place-items-center">
        <Heart className={cn(dim, on ? "fill-accent text-accent" : "text-ink")} strokeWidth={on ? 0 : 2} />
      </motion.span>
      <AnimatePresence>
        {pulse > 0 && on && BURST.map((a, i) => (
          <motion.span key={`${pulse}-${i}`} aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 size-1.5 rounded-full bg-accent"
            initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
            animate={{ x: Math.cos(a) * 26, y: Math.sin(a) * 26, opacity: 0, scale: 0.4 }}
            transition={{ duration: 0.6, ease: "easeOut" }} />
        ))}
      </AnimatePresence>
      {withLabel && <span className="text-sm font-medium">{on ? t("fav.remove") : t("fav.add")}</span>}
    </button>
  );
}
